// Photos and videos from VERA Field: what the team and crews showed VERA through their glasses, and what they uploaded
// from their phones, per workspace.
// Kept in VERA's private storage and deleted automatically after 12 months. People see only their own workspaces.

import { useCallback, useEffect, useState } from "react";
import { useStudio } from "./ui";
import { T } from "../lib/i18n";
import { cpFetch } from "../lib/controlPlane";
import { config } from "../config/env";
import { CONTROL_PLANE_WORKSPACE, type ScopeId } from "../config/model";
import { SCOPE_LABEL } from "../data/sample";
import { vera } from "../api/vera";

type Photo = { id: string; workspace: string; takenBy: string; note: string; takenAt: string; size: number; kind?: "photo" | "video" };
const isVideo = (p: Photo) => p.kind === "video";
const MAX_SHOWN = 60;

export function FieldPhotos() {
  const { getToken, profile, go } = useStudio();
  const scopes = profile.scopes.filter((s) => CONTROL_PLANE_WORKSPACE[s]);
  // Field workspaces first: VDS and Marine are where the crews work.
  const ordered = [...scopes].sort((a, b) => Number(!["vds", "marine"].includes(a)) - Number(!["vds", "marine"].includes(b)));
  const [scope, setScope] = useState<ScopeId | undefined>(ordered[0]);
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [images, setImages] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<Photo | null>(null);
  // A video is fetched only when it's opened (it can be up to 100 MB), and played from memory.
  const [video, setVideo] = useState<{ id: string; url: string } | null>(null);
  useEffect(() => {
    if (!open || !isVideo(open)) return;
    let live = true, url = "";
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${config.controlPlaneUrl}/v1/field/photo?id=${encodeURIComponent(open.id)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        url = URL.createObjectURL(await res.blob());
        if (live) setVideo({ id: open.id, url }); else URL.revokeObjectURL(url);
      } catch { /* stays on Loading */ }
    })();
    return () => { live = false; if (url) URL.revokeObjectURL(url); setVideo(null); };
  }, [open, getToken]);
  const [error, setError] = useState("");
  const workspaceId = scope ? CONTROL_PLANE_WORKSPACE[scope]!.workspaceId : "";

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setPhotos(null); setError("");
    try { setPhotos((await cpFetch<{ photos: Photo[] }>(getToken, `/v1/field/photos?workspaceId=${encodeURIComponent(workspaceId)}`)).photos.slice(0, MAX_SHOWN)); }
    catch (e) { setError(e instanceof Error ? e.message : T("Couldn't load photos from the field.")); setPhotos([]); }
  }, [getToken, workspaceId]);
  useEffect(() => { if (vera.mode === "live") void load(); }, [load]);

  // Each photo is fetched with the person's own sign-in and shown as a data URL (so it can also go to VERA).
  useEffect(() => {
    let live = true;
    void (async () => {
      for (const p of photos ?? []) {
        if (!live || images[p.id] || isVideo(p)) continue;
        try {
          const token = await getToken();
          const res = await fetch(`${config.controlPlaneUrl}/v1/field/photo?id=${encodeURIComponent(p.id)}`, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) continue;
          const blob = await res.blob();
          const url = await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = () => reject(r.error); r.readAsDataURL(blob); });
          if (live) setImages((m) => ({ ...m, [p.id]: url }));
        } catch { /* leave that one blank */ }
      }
    })();
    return () => { live = false; };
  }, [photos, getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  function askVera(p: Photo) {
    const dataUrl = images[p.id];
    if (!dataUrl) return;
    go("workspace", {
      ask: `This photo came from ${p.takenBy || "a teammate"}'s glasses on ${new Date(p.takenAt).toLocaleString()}.${p.note ? ` VERA's note at the time: "${p.note}".` : ""} What do you see, and is there anything we should follow up on?`,
      project: scope,
      fresh: true,
      title: `Field photo from ${p.takenBy || "the field"}`.slice(0, 60),
      files: [{ name: "field-photo.jpg", text: "", kind: "image", mime: "image/jpeg", dataUrl, thumb: dataUrl, size: p.size }],
    });
  }

  if (vera.mode !== "live") return <p className="mute">{T("Photos from VERA Field appear here with live VERA.")}</p>;
  if (!scope) return <p className="mute">{T("Your role doesn't include a workspace with field photos.")}</p>;
  return (
    <section className="card" style={{ padding: 16 }}>
      <div className="row" style={{ justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <b>{T("From the field")}</b>
          <p className="faint" style={{ margin: "4px 0 0", fontSize: 12.5 }}>{T("What people showed VERA through their glasses, with her note. Deleted automatically after 12 months.")}</p>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {ordered.map((s) => <button key={s} className={`chip ${s === scope ? "on" : ""}`} onClick={() => setScope(s)}>{SCOPE_LABEL[s]}</button>)}
          <button className="btn" onClick={() => void load()}>{T("Refresh")}</button>
        </div>
      </div>
      {error && <p style={{ color: "var(--bad)" }}>{error}</p>}
      {photos === null ? <p className="faint">{T("Loading…")}</p>
        : !photos.length ? <p className="faint" style={{ marginTop: 12 }}>{T("No photos from the field in")}{" "}{SCOPE_LABEL[scope]}{" "}{T("yet. They appear here when someone says \"VERA, look\" in VERA Field.")}</p>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginTop: 12 }}>
            {photos.map((p) => (
              <button key={p.id} className="card" style={{ padding: 0, overflow: "hidden", textAlign: "left", background: "var(--panel)" }} onClick={() => setOpen(p)}>
                {isVideo(p) ? <div style={{ width: "100%", aspectRatio: "3 / 4", background: "rgba(255,255,255,.06)", display: "grid", placeItems: "center", fontSize: 13 }} aria-label={T("Video from the field")}>▶ {T("Video from the field")}</div>
                  : images[p.id] ? <img src={images[p.id]} alt={p.note || T("Photo from the field")} style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", display: "block" }} />
                  : <div style={{ width: "100%", aspectRatio: "3 / 4", background: "rgba(255,255,255,.04)" }} />}
                <div style={{ padding: "8px 10px" }}>
                  <b style={{ fontSize: 13 }}>{p.takenBy || T("Someone on the team")}</b>
                  <div className="faint" style={{ fontSize: 12 }}>{new Date(p.takenAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      {open && (
        <div className="quick-back" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="card quick" role="dialog" aria-modal="true" aria-label={T("Photo from the field")} style={{ maxWidth: 620 }}>
            {isVideo(open)
              ? (video?.id === open.id ? <video src={video.url} controls playsInline autoPlay style={{ width: "100%", maxHeight: "60vh", borderRadius: 12, background: "#000" }} /> : <p className="faint">{T("Loading…")}</p>)
              : images[open.id] && <img src={images[open.id]} alt={open.note || T("Photo from the field")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12 }} />}
            <div className="faint" style={{ fontSize: 12.5 }}>{open.takenBy || T("Someone on the team")} · {new Date(open.takenAt).toLocaleString()} · {SCOPE_LABEL[scope]}</div>
            {open.note && <p><b>{T("VERA's note:")}</b> {open.note}</p>}
            <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
              <button className="btn" onClick={() => setOpen(null)}>{T("Close")}</button>
              {!isVideo(open) && <button className="btn primary" disabled={!images[open.id]} onClick={() => askVera(open)}>{T("Ask VERA about this")}</button>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
